import React, {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { videoUploader } from "../../../utils/Common/MediaUploader";
import { getSessionStorageItem } from "../../../utils/SessionStorageMethod/getSessionStorageItem";
import { setSessionStorageItem } from "../../../utils/SessionStorageMethod/setSessionStorageItem";
import {
  AudioBooleanSettings,
  AudioSettings,
  VideoFrameRate,
  VideoResolution,
} from "../../../utils/Enums/AudioVideoSettings";
import * as faceapi from "face-api.js";
import { showToast } from "ui-components";
import { insertAIVisualAnalysis } from "../../../service/SubmitAssessment/SubmitAssessment";
interface Recorder {
  SpeechRecognition?: any;
  timer?: number;
  currentQuestionIndex?: number;
  questionId?: number | string;
  setVideoUrl?: any;
  isCodingQuestion?: boolean;
  data: any;
  handleSubmitAnswer?: any;
  setStartQuestionLoader: any;
  startQuestionLoader: boolean;
  isStartRecording: boolean;
  cameraStopped: boolean;
}
const Recorder: React.ForwardRefRenderFunction<any, Recorder> = (
  {
    SpeechRecognition,
    timer,
    currentQuestionIndex,
    questionId,
    isCodingQuestion = false,
    data,
    handleSubmitAnswer,
    setStartQuestionLoader,
    startQuestionLoader,
    isStartRecording,
    cameraStopped,
  },
  ref,
) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorder = useRef<MediaRecorder | any>(null);
  const audioRecorder = useRef<MediaRecorder | any>(null);
  const [recordingStopped, setRecordingStopped] = useState(false);
  const intervalRef = useRef<any>(null);
  // Gaze & face tracking counters
  const gazeCountsRef = useRef({
    left: 0,
    right: 0,
    up: 0,
    down: 0,
    total: 0,
    noFace: 0,
    multipleFaces: 0,
  });
  // function to get the expression in percentage
  const getEmotionAverages = (
    durations: Record<string, number>,
    currentExpression: string,
    expressionStart: number,
  ): { "Emotion Averages": Record<string, number> } => {
    console.log("currentExpression after submit", currentExpression);

    const now = Date.now();
    const duration = now - expressionStart;
    const durationsCopy = { ...durations };
    if (durationsCopy[currentExpression] !== undefined) {
      durationsCopy[currentExpression] += duration;
    }
    const total = Object.values(durationsCopy).reduce((a, b) => a + b, 0);
    const percentages: Record<string, number> = {};
    for (const key in durationsCopy) {
      percentages[key] = total
        ? +((durationsCopy[key] / total) * 100).toFixed(2)
        : 0;
    }
    return { "Emotion Averages": percentages };
  };
  const expressionDurationsRef = useRef<Record<string, number>>({
    ANGRY: 0,
    CALM: 0,
    DISGUSTED: 0,
    FEAR: 0,
    HAPPY: 0,
    SAD: 0,
    SURPRISED: 0,
    CONFUSED: 0,
  });
  const insertAIVisualAnalysisApi = async (
    companyId: any,
    assessmentId: any,
    applicantId: any,
    questionId: any,
  ) => {
    const payload = {
      company_id: companyId,
      assessment_id: assessmentId,
      applicant_id: applicantId,
      question_id: questionId,
      result: {
        face_emotions_cont: getEmotionAverages(
          expressionDurationsRef.current,
          currentExpressionRef.current,
          expressionStartTimeRef.current,
        ),
      },
    };
    await insertAIVisualAnalysis(payload);
  };
  useImperativeHandle(ref, () => ({
    insertAIVisualAnalysisApi,
  }));
  const currentExpressionRef = useRef<string>("CALM");
  const expressionStartTimeRef = useRef<number>(Date.now());
  const setupCamera = async () => {
    try {
      const audioConstraints = startQuestionLoader
        ? false
        : {
            sampleRate: { ideal: AudioSettings.SampleRateIdeal || 44100 },
            sampleSize: { ideal: AudioSettings.SampleSizeIdeal || 16 },
            channelCount: { ideal: AudioSettings.ChannelCountIdeal || 1 },
            echoCancellation: AudioBooleanSettings.EchoCancellation ?? true,
            noiseSuppression: AudioBooleanSettings.NoiseSuppression ?? true,
            autoGainControl: AudioBooleanSettings.AutoGainControl ?? true,
          };

      const videoConstraints = startQuestionLoader
        ? false
        : {
            width: {
              ideal: VideoResolution.WidthIdeal || 1280,
              max: VideoResolution.WidthMax || 1920,
            },
            height: {
              ideal: VideoResolution.HeightIdeal || 720,
              max: VideoResolution.HeightMax || 1080,
            },
            frameRate: {
              ideal: VideoFrameRate.FrameRateIdeal || 30,
              max: VideoFrameRate.FrameRateMax || 60,
            },
            facingMode: "user",
          };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: videoConstraints,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => videoRef.current?.play();
      }
      // Load face-api models
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      await faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL);

      // Start tracking gaze & expression
      intervalRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState !== 4) return;

        const detection = await faceapi
          .detectAllFaces(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions(),
          )
          .withFaceLandmarks()
          .withFaceExpressions();

        // Gaze & face count logic
        if (!detection || detection.length === 0) {
          gazeCountsRef.current.noFace += 1;
          gazeCountsRef.current.total += 1;
          if (gazeCountsRef.current.noFace >= 5) {
            showToast("No face detected", "info", 100, () => {}, "top-center");
            gazeCountsRef.current = {
              left: 0,
              right: 0,
              up: 0,
              down: 0,
              total: 0,
              noFace: 0,
              multipleFaces: 0,
            };
          }
          return;
        }

        if (detection.length > 1) {
          gazeCountsRef.current.multipleFaces += 1;
          gazeCountsRef.current.total += 1;
          if (gazeCountsRef.current.multipleFaces >= 1) {
            showToast(
              "Multiple faces detected",
              "info",
              100,
              () => {},
              "top-center",
            );
            gazeCountsRef.current = {
              left: 0,
              right: 0,
              up: 0,
              down: 0,
              total: 0,
              noFace: 0,
              multipleFaces: 0,
            };
          }
          return;
        }

        // Get first face landmarks for gaze tracking
        const landmarks = detection[0].landmarks;
        const leftEye = landmarks.getLeftEye();
        const rightEye = landmarks.getRightEye();
        const leftIris = leftEye[0]; // approximate
        const eyeBox = {
          left: leftEye[0],
          right: rightEye[3],
          top: leftEye[1],
          bottom: leftEye[4],
        };

        const gaze = getGazeDirectionRelative(leftIris, eyeBox);
        if (gaze !== "center") {
          gazeCountsRef.current[gaze] += 1;
          gazeCountsRef.current.total += 1;
          if (gazeCountsRef.current[gaze] >= 5) {
            showToast(
              "Please look at center",
              "info",
              200,
              () => {},
              "top-center",
            );
            gazeCountsRef.current = {
              left: 0,
              right: 0,
              up: 0,
              down: 0,
              total: 0,
              noFace: 0,
              multipleFaces: 0,
            };
          }
        }

        // Face expression logic
        if (detection[0].expressions) {
          const entries = Object.entries(detection[0].expressions);
          const [topExpression] = entries.reduce((prev, curr) =>
            curr[1] > prev[1] ? curr : prev,
          );
          // Map face-api.js expression to your existing keys
          const faceApiToYourKeys: Record<string, string> = {
            neutral: "CALM",
            happy: "HAPPY",
            sad: "SAD",
            angry: "ANGRY",
            fearful: "FEAR",
            disgusted: "DISGUSTED",
            surprised: "SURPRISED",
            // optional: map smirk or confused if needed
          };
          const mappedExpression = faceApiToYourKeys[topExpression] || "CALM";
          const now = Date.now();
          const prevExpr = currentExpressionRef.current;
          const duration = now - expressionStartTimeRef.current;

          if (expressionDurationsRef.current[prevExpr] !== undefined) {
            expressionDurationsRef.current[prevExpr] += duration;
          }
          currentExpressionRef.current = mappedExpression;
          expressionStartTimeRef.current = now;
        }
      }, 1000);

      // Setup media recorder
      mediaRecorder.current = new MediaRecorder(stream);
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        audioRecorder.current = new MediaRecorder(
          new MediaStream([audioTracks[0]]),
        );
      }

      if (mediaRecorder.current.state === "inactive" && isStartRecording) {
        mediaRecorder.current.start();
        audioRecorder.current?.start();
        setRecordingStopped(true);
        SpeechRecognition.startListening({ continuous: true });
      }
    } catch (err) {
      console.error("Camera setup failed:", err);
    }
  };

  useEffect(() => {
    if (cameraStopped) {
      stopCamera();
      stopRecording();
    }
  }, [cameraStopped]);

  useEffect(() => {
    setupCamera();
    return () => {
      clearInterval(intervalRef.current);
      if (mediaRecorder.current?.state === "recording")
        mediaRecorder.current.stop();
      if (audioRecorder.current?.state === "recording")
        audioRecorder.current.stop();
    };
  }, [currentQuestionIndex, questionId, isStartRecording]);

  useEffect(() => {
    const checkMediaDevices = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: startQuestionLoader ? false : true,
          video: startQuestionLoader ? false : true,
        });

        // Stop the stream to release resources
        stream.getTracks().forEach((track) => track.stop());

        if (timer === 0) {
          await stopRecording();
          stopCamera();
        }
      } catch (error) {
        console.error(error);
      }
    };
    checkMediaDevices();
  }, [timer, recordingStopped]);

  const getGazeDirectionRelative = (iris: any, eyeBox: any) => {
    const xMin = eyeBox.left.x;
    const xMax = eyeBox.right.x;
    const yMin = eyeBox.top.y;
    const yMax = eyeBox.bottom.y;

    const xRatio = (iris.x - xMin) / (xMax - xMin);
    const yRatio = (iris.y - yMin) / (yMax - yMin);

    if (xRatio < 0.25) return "right";
    if (xRatio > 0.65) return "left";
    if (yRatio < 0.38) return "down";
    if (yRatio > 0.45) return "up";
    return "center";
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject as MediaStream;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current!.srcObject = null;
    }
  };

  const stopRecording = async () => {
    return new Promise<void>((resolve) => {
      const chunks: Blob[] = [];
      const audioChunks: Blob[] = [];
      mediaRecorder.current.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      audioRecorder.current.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      let mediaStopped = false;
      let audioStopped = false;

      const maybeFinish = async () => {
        if (!mediaStopped || !audioStopped) return;

        const blob = new Blob(chunks, { type: "video/webm" });
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
        const skipStatus = getSessionStorageItem("isStatusUpdate");
        const isReportStatus = getSessionStorageItem("isReport");
        if (
          !isReportStatus &&
          skipStatus !== "skipped" &&
          data?.type !== "Coding"
        ) {
          setStartQuestionLoader(true);
          console.log("upload");
          const videoS3Uploaded = await videoUploader(
            blob,
            `${data?.mediaPath}${questionId}`,
          );
          await videoUploader(
            audioBlob,
            `${data?.mediaPath}${questionId}/${questionId}`,
          );
          handleSubmitAnswer(videoS3Uploaded?.fileName);
        } else {
          handleSubmitAnswer("");
          setSessionStorageItem("isReport", false);
        }
        resolve();
      };

      mediaRecorder.current.onstop = () => {
        mediaStopped = true;
        maybeFinish();
      };
      audioRecorder.current.onstop = () => {
        audioStopped = true;
        maybeFinish();
      };

      mediaRecorder.current?.stop();
      audioRecorder.current?.stop();
      setRecordingStopped(false);
    });
  };

  return (
    <>
      {isCodingQuestion ? (
        <video
          style={{ marginLeft: "28px" }}
          width={250}
          ref={videoRef}
          muted
          autoPlay={true}
        />
      ) : (
        <video ref={videoRef} muted autoPlay={true} />
      )}
    </>
  );
};

export default forwardRef(Recorder);
